import { prisma, type Prisma, ResourceType, ActionType } from "client";

// READ

export const getEventGroup = async (userId: string, id: string) => {
  try {
        const eventGroup = await prisma.eventGroup.findUnique({
      where: { id },
    });
    return eventGroup;
  } catch (error: any) {
    return error;
  }
};

export const getEventGroupByWhiteLabel = async (
  userId: string,
  whiteLabelId: string
) => {
  try {
        const eventGroups = await prisma.eventGroup.findMany({
      where: {
        whiteLabelId,
        disabled: false,
      },
      include: {
        events: true,
      },
    });
    const eventGroupsWithEmptyEvents = eventGroups.map((eventGroup) => {
      if (!eventGroup.events || eventGroup.events.length === 0) {
        return {
          ...eventGroup,
          events: [],
        };
      }
      return eventGroup;
    });
    return eventGroupsWithEmptyEvents;
  } catch (error: any) {
    return error;
  }
};

type ThenArg<T> = T extends PromiseLike<infer U> ? U : T;
export type EventGroupWithEventsData = ThenArg<
  ReturnType<typeof getEventGroupByWhiteLabel>
>;
export type EventGroupWithEvents = Prisma.PromiseReturnType<
  typeof getEventGroupByWhiteLabel
>;

// CREATE
export const createEventGroup = async (
  userId: string,
  eventIds: string[],
  eventGroupPayload: Prisma.EventGroupCreateInput
) => {
  try {
        const payload = {
      ...eventGroupPayload,
    };
    if (eventIds.length)
      payload.events = {
        connect: eventIds.map((id) => ({ id })),
      };
    const eventGroup = await prisma.eventGroup.create({
      data: { ...payload },
    });
    return eventGroup;
  } catch (error: any) {
    return error;
  }
};

// UPDATE
export const updateEventGroup = async (
  userId: string,
  id: string,
  eventIds: string[],
  updatedData: Prisma.EventGroupUpdateInput
) => {
  try {
        // Fetch the existing event group data to get the current event IDs
    const existingEventGroup = await prisma.eventGroup.findUnique({
      where: {
        id,
      },
      include: {
        events: {
          select: {
            id: true,
          },
        },
      },
    });

    let existingEventIds: any[] = [];
    let eventIdsToDisconnect = [];
    let eventIdsToConnect: any[] = [];

    if (existingEventGroup) {
      existingEventIds = existingEventGroup.events.map((event) => event.id);
    }

    if (existingEventIds.length > 0) {
      eventIdsToDisconnect = existingEventIds.filter(
        (eventId) => !eventIds.includes(eventId)
      );
    }

    if (eventIds.length > 0) {
      eventIdsToConnect = eventIds.filter(
        (eventId) => !existingEventIds.includes(eventId)
      );
    }

    const eventGroup = await prisma.eventGroup.update({
      where: {
        id,
      },
      data: {
        ...updatedData,
        events: {
          disconnect: eventIdsToDisconnect.map((eventId) => ({ id: eventId })),
          connect: eventIdsToConnect.map((eventId) => ({ id: eventId })),
        },
      },
    });

    return eventGroup;
  } catch (error: any) {
    return error;
  }
};

// DELETE
export const deleteEventGroup = async (userId: string, id: string) => {
  try {
        const eventGroup = await prisma.eventGroup.findUnique({
      where: {
        id,
      },
      include: {
        events: true,
      },
    });

    if (!eventGroup) {
      throw new Error(`Event Group with ID ${id} not found.`);
    }

    const eventIds = eventGroup.events.map((event) => event.id);

    await prisma.eventGroup.update({
      where: {
        id,
      },
      data: {
        disabled: true,
        events: {
          disconnect: eventIds.map((eventId) => ({ id: eventId })),
        },
      },
    });

    return eventGroup;
  } catch (error: any) {
    return error;
  }
};
